import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Loader2, Package, CheckCircle2, AlertTriangle, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_MESSAGES = {
  EXPIRED: { title: 'Einladung abgelaufen', body: 'Diese Einladung ist nicht mehr gültig. Bitte fordern Sie eine neue an.' },
  ACCEPTED: { title: 'Bereits angenommen', body: 'Diese Einladung wurde bereits genutzt. Bitte loggen Sie sich ein.' },
  DECLINED: { title: 'Einladung abgelehnt', body: 'Diese Einladung wurde bereits abgelehnt.' },
  REVOKED: { title: 'Einladung zurückgezogen', body: 'Diese Einladung wurde vom Absender zurückgezogen.' },
  SUPERSEDED: { title: 'Einladung ersetzt', body: 'Es gibt eine neuere Einladung — bitte schauen Sie in Ihrem Posteingang.' },
};

export default function InvitationAcceptPage() {
  const { invitationId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    name: '',
    company_name: '',
    password: '',
    confirm: '',
    accept_terms: false,
  });

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/invitations/by-token/${invitationId}`);
        if (!cancel) setInvitation(res.data);
      } catch (e) {
        if (!cancel) setError(e?.response?.data?.detail || 'Einladung konnte nicht geladen werden');
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [invitationId]);

  const update = (key) => (e) => {
    const val = e?.target?.type === 'checkbox' ? e.target.checked : e?.target?.value;
    setForm((prev) => ({ ...prev, [key]: val }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error('Passwort muss mindestens 8 Zeichen haben');
      return;
    }
    if (form.password !== form.confirm) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }
    if (!form.accept_terms) {
      toast.error('Bitte stimmen Sie den AGB zu');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/invitations/by-token/${invitationId}/register-and-accept`, {
        name: form.name.trim(),
        password: form.password,
        company_name: form.company_name.trim() || null,
        accept_terms: true,
      });
      // Auto-login: persist token + redirect
      const { access_token } = res.data;
      if (access_token) {
        localStorage.setItem('token', access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      }
      toast.success(`Willkommen bei Surplify, ${form.name.split(' ')[0]}!`);
      // Hard navigation so AuthContext re-bootstraps with the new token
      window.location.assign('/dashboard');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Registrierung fehlgeschlagen');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Loading ───────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950" data-testid="invitation-loading">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  // ─── Error / not-found ─────────────────────────────────────────────
  if (error || !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 px-4">
        <Card className="w-full max-w-md" data-testid="invitation-error">
          <CardHeader>
            <div className="flex items-center gap-3 text-red-600">
              <AlertTriangle className="h-6 w-6" />
              <CardTitle>Einladung nicht gefunden</CardTitle>
            </div>
            <CardDescription>{error || 'Der Link ist ungültig oder die Einladung wurde entfernt.'}</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/">
              <Button variant="outline" className="w-full" data-testid="invitation-error-home-btn">Zurück zur Startseite</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── Status: anything but PENDING ─────────────────────────────────
  if (invitation.status !== 'PENDING') {
    const msg = STATUS_MESSAGES[invitation.status] || { title: 'Einladung nicht offen', body: 'Diese Einladung kann nicht angenommen werden.' };
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 px-4">
        <Card className="w-full max-w-md" data-testid="invitation-not-pending">
          <CardHeader>
            <div className="flex items-center gap-3 text-amber-600">
              <AlertTriangle className="h-6 w-6" />
              <CardTitle>{msg.title}</CardTitle>
            </div>
            <CardDescription>{msg.body}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/login">
              <Button className="w-full bg-orange-500 hover:bg-orange-600" data-testid="invitation-status-login-btn">Zum Login</Button>
            </Link>
            <Link to="/">
              <Button variant="outline" className="w-full">Zur Startseite</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── User already has an account → suggest login instead ──────────
  if (invitation.user_exists) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 px-4">
        <Card className="w-full max-w-lg" data-testid="invitation-existing-user">
          <CardHeader className="text-center">
            <Link to="/" className="flex items-center justify-center gap-2 mb-4">
              <Package className="h-10 w-10 text-orange-500" />
              <span className="font-manrope font-bold text-xl">Surplify</span>
            </Link>
            <CardTitle>Sie haben bereits ein Konto</CardTitle>
            <CardDescription>
              Unter <strong>{invitation.invited_email}</strong> existiert bereits ein Surplify-Account.
              Loggen Sie sich ein — die Einladung zur Gruppe „{invitation.group_name}" liegt in Ihrem Portal.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to={`/login?next=/subscriptions`}>
              <Button className="w-full bg-orange-500 hover:bg-orange-600" data-testid="invitation-go-login-btn">
                Jetzt einloggen <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── Main registration form ───────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 py-12 px-4" data-testid="invitation-register-page">
      <div className="max-w-2xl mx-auto">
        {/* Hero */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-4">
            <Package className="h-10 w-10 text-orange-500" />
            <span className="font-manrope font-bold text-2xl text-slate-900 dark:text-slate-50">Surplify</span>
          </Link>
          <h1 className="font-manrope text-3xl sm:text-4xl font-bold text-slate-900 dark:text-slate-50">
            Willkommen bei Surplify.de
          </h1>
          <p className="text-slate-600 dark:text-slate-300 mt-2">
            Sie wurden zur Gruppe <strong className="text-orange-600">„{invitation.group_name}"</strong> eingeladen
          </p>
        </div>

        {/* Group context card */}
        <Card className="mb-6 border-orange-200 dark:border-orange-900 bg-orange-50/50 dark:bg-orange-950/20" data-testid="invitation-group-context">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-slate-500 dark:text-slate-400">Einladender</p>
                <p className="font-semibold text-slate-900 dark:text-slate-50">{invitation.inviting_user_name || '—'}</p>
              </div>
              <div>
                <p className="text-slate-500 dark:text-slate-400">Tier</p>
                <p className="font-semibold text-slate-900 dark:text-slate-50">{invitation.group_tier_label || 'Standard'}</p>
              </div>
              <div>
                <p className="text-slate-500 dark:text-slate-400">Provision</p>
                <p className="font-semibold text-slate-900 dark:text-slate-50">
                  {invitation.group_commission_rate != null ? `${invitation.group_commission_rate}%` : '—'}
                </p>
              </div>
            </div>
            {invitation.personal_message && (
              <div className="mt-4 p-3 bg-white dark:bg-slate-900 border border-orange-200 dark:border-orange-900 rounded text-sm italic text-slate-700 dark:text-slate-300">
                „{invitation.personal_message}"
              </div>
            )}
          </CardContent>
        </Card>

        {/* Form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Konto erstellen & Einladung annehmen</CardTitle>
            <CardDescription>
              In 60 Sekunden startklar — wir hängen Sie direkt an die Gruppe an, sobald Sie auf „Registrieren" klicken.
            </CardDescription>
          </CardHeader>
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs font-medium">E-Mail-Adresse</Label>
                <Input
                  type="email"
                  value={invitation.invited_email}
                  disabled
                  data-testid="invitation-email-input"
                  className="bg-slate-100 dark:bg-slate-800"
                />
                <p className="text-[11px] text-slate-500 mt-1">Aus der Einladung übernommen — kann nicht geändert werden</p>
              </div>
              <div>
                <Label className="text-xs font-medium">Ihr vollständiger Name *</Label>
                <Input
                  required
                  value={form.name}
                  onChange={update('name')}
                  placeholder="Max Mustermann"
                  data-testid="invitation-name-input"
                />
              </div>
              <div>
                <Label className="text-xs font-medium">Firmenname (optional)</Label>
                <Input
                  value={form.company_name}
                  onChange={update('company_name')}
                  placeholder="Acme Packaging GmbH"
                  data-testid="invitation-company-input"
                />
                <p className="text-[11px] text-slate-500 mt-1">Wird als Standort-Name angelegt. Lassen Sie das Feld leer, wenn Ihre Gruppe den Standort später verwaltet.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-medium">Passwort *</Label>
                  <Input
                    required
                    type="password"
                    minLength={8}
                    value={form.password}
                    onChange={update('password')}
                    placeholder="Mind. 8 Zeichen"
                    data-testid="invitation-password-input"
                  />
                </div>
                <div>
                  <Label className="text-xs font-medium">Passwort wiederholen *</Label>
                  <Input
                    required
                    type="password"
                    value={form.confirm}
                    onChange={update('confirm')}
                    placeholder="Erneut eingeben"
                    data-testid="invitation-confirm-input"
                  />
                </div>
              </div>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.accept_terms}
                  onChange={update('accept_terms')}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-orange-500"
                  data-testid="invitation-accept-terms"
                />
                <span className="text-xs text-slate-600 dark:text-slate-400">
                  Ich habe die <Link to="/legal/agb" target="_blank" className="text-orange-600 underline">AGB</Link> und
                  die <Link to="/legal/datenschutz" target="_blank" className="text-orange-600 underline">Datenschutzerklärung</Link> gelesen und stimme zu.
                </span>
              </label>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white py-6 text-base font-semibold"
                data-testid="invitation-submit-btn"
              >
                {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                  <>
                    <CheckCircle2 className="mr-2 h-5 w-5" />
                    Konto erstellen & der Gruppe beitreten
                  </>
                )}
              </Button>
              <p className="text-center text-xs text-slate-500 dark:text-slate-400">
                Bereits ein Konto? <Link to={`/login?next=/subscriptions`} className="text-orange-600 underline">Hier einloggen</Link>
              </p>
            </CardContent>
          </form>
        </Card>
      </div>
    </div>
  );
}
